import React, { useRef, useState, useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Text,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Grid,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import baseUrl from 'Base_Url/baseUrl';
import axiosInstance from 'axiosInstance';

// For Graphs
import {
  LineChart,
  Legend,
  Line,
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { useUser } from '../../../userContext';

// For excel downloads
import * as XLSX from 'xlsx';

import { ReactComponent as PdfDownload } from '../../../assets/icons/PDF.svg';
import { ReactComponent as XlDownload } from '../../../assets/icons/XL Icon.svg';
import { notifyApiError } from 'utils/apiFeedback';

const ABSTRACTION_PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const ABSTRACTION_PERIOD_LABELS = ABSTRACTION_PERIOD_OPTIONS.reduce(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {},
);

const AnalyticsDashboard = () => {
  const { updateLicenseData } = useUser();
  const userRole = localStorage.getItem('role');
  const [userPermissions, setUserPermissions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [activeTabEnergyConsumption, setactiveTabEnergyConsumption] =
    useState(0);
  const [selectedPid, setSelectedPid] = useState('');
  const [startDate, setStartDate] = useState('');
  const [stopDate, setStopDate] = useState('');
  const [abstractionPeriod, setAbstractionPeriod] = useState('daily');

  const [startDateEnergyCons, setStartDateEnergyCons] = useState('');

  const [dailyAbstraction, setDailyAbstraction] = useState([]);
  const [analyticsLogs, setAnalyticsLogs] = useState([]);
  const [energyConsumptionGraph, setenergyConsumptionGraph] = useState([]);

  const [avg_vll, setAvgVll] = useState([]);
  const [current, setCurrent] = useState([]);
  const [powerFactor, setPowerFactor] = useState([]);
  const [frequency, setFrequency] = useState([]);
  const [voltage, setVoltage] = useState([]);
  const [kwh, setKwh] = useState([]);
  const [kw, setKw] = useState([]);
  const [loading, setLoading] = useState(false);

  const [deviceList, setDeviceList] = useState([]);
  const selectedCompany = useRef('');

  const Token = localStorage.getItem('token');
  const toast = useToast();
  const IST_TIMEZONE = 'Asia/Kolkata';

  const [canMonitor, setCanMonitor] = useState(true);
  const [companyData, setCompanyData] = useState([]);

  const chartRef0 = useRef();
  const chartRef1 = useRef();
  const chartRef2 = useRef();

  // Refs for energy consumption graphs
  const ecChartRef0 = useRef();
  const ecChartRef1 = useRef();
  const ecChartRef2 = useRef();
  const ecChartRef3 = useRef();
  const ecChartRef4 = useRef();
  const ecChartRef5 = useRef();
  const ecChartRef6 = useRef();

  const allTabs = [
    {
      label: 'Water Abstraction for Borewell',
      key: 'borewell_abstraction',
    },
    {
      label: 'Water Abstraction for Plant',
      key: 'plant_abstraction',
    },
    {
      label: 'Borewell Operation Log',
      key: 'operation_log',
    },
    {
      label: 'Energy Consumption',
      key: 'energy_consumption',
    },
    {
      label: 'Alert Log',
      key: 'alert_log',
    },
  ];

  // hide these for baseuser
  const restrictedTabs = ['operation_log', 'energy_consumption', 'alert_log'];

  const visibleTabs =
    userRole === 'baseuser'
      ? allTabs.filter((tab) => !restrictedTabs.includes(tab.key))
      : allTabs;

  const abstractionPeriodLabel =
    ABSTRACTION_PERIOD_LABELS[abstractionPeriod] || 'Daily';
  const abstractionDateHeader =
    abstractionPeriod === 'daily' ? 'Date' : 'Period';
  const abstractionConsumptionHeader = `${abstractionPeriodLabel} Consumption`;
  const formatBarValueLabel = (value) => {
    if (typeof value === 'string' && value.trim() !== '') {
      const numericValue = Number(value);
      return Number.isFinite(numericValue)
        ? Number(numericValue.toFixed(3)).toString()
        : value;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '';

    return Number(numericValue.toFixed(3)).toString();
  };

  const renderDownloadOnlyBarLabel = ({ x, y, width, height, value }) => {
    const label = formatBarValueLabel(value);
    const numericX = Number(x);
    const numericY = Number(y);
    const numericWidth = Number(width);
    const numericHeight = Number(height);

    if (
      !label ||
      !Number.isFinite(numericX) ||
      !Number.isFinite(numericY) ||
      !Number.isFinite(numericWidth) ||
      !Number.isFinite(numericHeight)
    ) {
      return null;
    }

    const labelY = numericY + Math.max(10, Math.min(numericHeight / 2, 18));

    return (
      <text
        className="download-only-bar-label"
        data-download-bar-label="true"
        x={numericX + numericWidth / 2}
        y={labelY}
        fill="transparent"
        fontSize={12}
        fontWeight={700}
        textAnchor="middle"
        dominantBaseline="middle"
        pointerEvents="none"
      >
        {label}
      </text>
    );
  };

  const abstractionChartData = dailyAbstraction
    .map((item) => ({
      date: formatAbstractionChartDate(item.date),
      sortTime: getMomentValue(item.date)?.valueOf() || 0,
      Unit: Number(item.dailyConsumption) || 0,
    }))
    .sort((a, b) => a.sortTime - b.sortTime)
    .map(({ sortTime, ...item }) => item);

  const borewellOperationChartData = Object.values(
    analyticsLogs
      .filter((item) => item.type === 'operation') // ✅ Only include operation logs
      .reduce((acc, item) => {
        const formattedDate = formatBackendDate(item.date, 'DD-MMM');
        const key = `${item.pid}_${formattedDate}`;

        // Parse "0h 0m 11s" → total seconds
        const match = item.duration.match(/(\d+)h\s+(\d+)m\s+(\d+)s/);
        const hours = parseInt(match?.[1] || 0, 10);
        const minutes = parseInt(match?.[2] || 0, 10);
        const seconds = parseInt(match?.[3] || 0, 10);
        const durationSeconds = hours * 3600 + minutes * 60 + seconds;

        if (!acc[key]) {
          acc[key] = {
            pid: item.pid,
            date: formattedDate,
            value: 0,
          };
        }

        acc[key].value += durationSeconds;
        return acc;
      }, {}),
  )
    .sort((a, b) => moment(a.date, 'DD-MMM') - moment(b.date, 'DD-MMM'))
    .map((item) => {
      const hours = Math.floor(item.value / 3600);
      const minutes = Math.floor((item.value % 3600) / 60);
      const seconds = item.value % 60;
      const label = `${hours}h ${minutes}m ${seconds}s`;

      return {
        ...item,
        label, // human-readable duration
      };
    });

  const handleDownloadGraphPDF = async () => {
    let PdfHeadline;
    let chartElement;
    let fileName;
    switch (activeTab) {
      case 0:
        PdfHeadline = `Borewell Water Abstraction (${abstractionPeriodLabel} Report)`;
        chartElement = chartRef0.current;
        fileName = `Borewell_Water_Abstraction_Report_${
          selectedPid || 'N/A'
        }_${abstractionPeriodLabel}.pdf`;
        break;

      case 1:
        PdfHeadline = `Plant Water Abstraction (${abstractionPeriodLabel} Report)`;
        chartElement = chartRef1.current;
        fileName = `Plant_Water_Abstraction_Report_${abstractionPeriodLabel}.pdf`;
        break;

      case 2:
        PdfHeadline = 'Borewell Operation Logs (Daily Report)';
        chartElement = chartRef2.current;
        fileName = `Borewell_Operation_Logs_Report_${selectedPid || 'N/A'}.pdf`;
        break;

      default:
        PdfHeadline = 'N/A';
    }
    // const chartElement = chartRef.current;

    if (!chartElement) {
      return;
    }

    // Convert chart to canvas
    const canvas = await html2canvas(chartElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#fff',
      onclone: (clonedDocument) => {
        clonedDocument
          .querySelectorAll('[data-download-bar-label="true"]')
          .forEach((label) => {
            label.setAttribute('fill', '#fff');
            label.style.fill = '#fff';
          });
      },
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    // ✅ Add title and details at the top
    const marginTop = 10;
    pdf.setFontSize(16);

    pdf.text(PdfHeadline, pdfWidth / 2, marginTop, {
      align: 'center',
    });

    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);

    const generatedDate = `${formatNowIST()} IST`;

    pdf.text(`Product ID: ${selectedPid || 'Complete Plant'}`, 15, 25);
    pdf.text(
      `Date Range: ${startDate || 'N/A'} to ${stopDate || 'N/A'}`,
      15,
      32,
    );
    pdf.text(`Generated On: ${generatedDate}`, 15, 39);

    // Add chart image slightly below the details
    const chartYPosition = 45;
    pdf.addImage(imgData, 'PNG', 10, chartYPosition, pdfWidth - 20, pdfHeight);

    pdf.save(fileName);
  };

  const handleDownloadGraphPDFForEC = async () => {
    let PdfHeadline;
    let chartElement;
    let fileName;
    switch (activeTabEnergyConsumption) {
      case 0:
        PdfHeadline = 'Energy consumption Report VLL';
        chartElement = ecChartRef0.current;
        fileName = `Energy_consumption_Report_VLL_${selectedPid || 'N/A'}.pdf`;
        break;

      case 1:
        PdfHeadline = 'Energy consumption Report Current';
        chartElement = ecChartRef1.current;
        fileName = `Energy_consumption_Report_Current.pdf`;
        break;

      case 2:
        PdfHeadline = 'Energy consumption Report Power Factor';
        chartElement = ecChartRef2.current;
        fileName = `Energy_consumption_Report_Power_Factor_${
          selectedPid || 'N/A'
        }.pdf`;
        break;

      case 3:
        PdfHeadline = 'Energy consumption Report Frequency';
        chartElement = ecChartRef3.current;
        fileName = `Energy_consumption_Report_Frequency_${
          selectedPid || 'N/A'
        }.pdf`;
        break;

      case 4:
        PdfHeadline = 'Energy consumption Report Voltage';
        chartElement = ecChartRef4.current;
        fileName = `Energy_consumption_Report_Voltage_${
          selectedPid || 'N/A'
        }.pdf`;
        break;

      case 5:
        PdfHeadline = 'Energy consumption Report KWH';
        chartElement = ecChartRef5.current;
        fileName = `Energy_consumption_Report_Kwh_${selectedPid || 'N/A'}.pdf`;
        break;

      case 6:
        PdfHeadline = 'Energy consumption Report KW';
        chartElement = ecChartRef6.current;
        fileName = `Energy_consumption_Report_Kw_${selectedPid || 'N/A'}.pdf`;
        break;

      default:
        PdfHeadline = 'N/A';
    }
    // const chartElement = chartRef.current;

    if (!chartElement) {
      return;
    }

    // Convert chart to canvas
    const canvas = await html2canvas(chartElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#fff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    // ✅ Add title and details at the top
    const marginTop = 10;
    pdf.setFontSize(16);

    pdf.text(PdfHeadline, pdfWidth / 2, marginTop, {
      align: 'center',
    });

    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);

    const generatedDate = `${formatNowIST()} IST`;

    pdf.text(`Product ID: ${selectedPid || 'Complete Plant'}`, 15, 25);
    pdf.text(`For Date: ${startDateEnergyCons || 'N/A'}`, 15, 32);
    pdf.text(`Generated On: ${generatedDate}`, 15, 39);

    // Add chart image slightly below the details
    const chartYPosition = 45;
    pdf.addImage(imgData, 'PNG', 10, chartYPosition, pdfWidth - 20, pdfHeight);

    pdf.save(fileName);
  };

  const downloadExcelForBorewellAbstraction = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = `${formatNowIST()} IST`;

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`View By: ${abstractionPeriodLabel}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push([
      abstractionDateHeader,
      'Product ID',
      'Opening Timestamp',
      'Opening Value (Totalizer in m³)',
      'Closing Timestamp',
      'Closing Value (Totalizer in m³)',
      `${abstractionConsumptionHeader} (unit in m³)`,
    ]);

    // Table rows
    dailyAbstraction.forEach((item) => {
      wsData.push([
        formatAbstractionDate(item.date),
        item.pid,
        formatISODateTime(item.openingDate),
        item.openingTotalizer,
        formatISODateTime(item.closingDate),
        item.closingTotalizer,
        item.dailyConsumption,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      `${abstractionPeriodLabel} Abstraction`,
    );

    // Save as Excel file
    XLSX.writeFile(
      wb,
      `${abstractionPeriodLabel}_Abstraction_Report_${productId || 'Plant'}.xlsx`,
    );
  };

  const downloadExcelForPlantAbstraction = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = `${formatNowIST()} IST`;

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`View By: ${abstractionPeriodLabel}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push([
      abstractionDateHeader,
      'Opening Value (Totalizer in m³)',
      'Closing Value (Totalizer in m³)',
      `${abstractionConsumptionHeader} (unit in m³)`,
    ]);

    // Table rows
    dailyAbstraction.forEach((item) => {
      wsData.push([
        formatAbstractionDate(item.date),
        item.openingTotalizer,
        item.closingTotalizer,
        item.dailyConsumption,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      `${abstractionPeriodLabel} Abstraction`,
    );

    // Save as Excel file
    XLSX.writeFile(
      wb,
      `${abstractionPeriodLabel}_Abstraction_Report_Plant.xlsx`,
    );
  };

  const downloadExcelForBorewellConsumption = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = `${formatNowIST()} IST`;

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push([
      'Date',
      'Product ID',
      'Mode',
      'On Time',
      'Off Time',
      'Duration',
      'Source',
    ]);

    // Table rows
    analyticsLogs.forEach((item) => {
      wsData.push([
        formatISODate(item.date),
        item.pid,
        item.mode,
        formatTimeTo12Hour(item.onTime),
        formatTimeTo12Hour(item.offTime),
        item.duration,
        item.source,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

    // Save as Excel file
    XLSX.writeFile(
      wb,
      `Borewell_Consumption_Logs_${productId || 'Plant'}.xlsx`,
    );
  };

  const downloadExcelForEneryConsumption = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = `${formatNowIST()} IST`;

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push(['Date', 'Product ID', 'Unit Consumed (KWH)']);

    // Table rows
    analyticsLogs.forEach((item) => {
      wsData.push([formatISODate(item.date), item.pid, `${item.unitConsumed}`]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

    // Save as Excel file
    XLSX.writeFile(
      wb,
      `Borewell_Energy_Consumption_Logs_${productId || 'Plant'}.xlsx`,
    );
  };

  const downloadExcelForAlertLogs = () => {
    // Example variables
    const companyName = localStorage.getItem('companyName');
    // const productName = 'G-Block';
    const productId = selectedPid;
    const downloadDate = `${formatNowIST()} IST`;

    // Prepare worksheet data
    const wsData = [];

    // Top info rows
    wsData.push([`Company Name: ${companyName}`]);
    // wsData.push([`Product Name: ${productName}`]);
    wsData.push([`Product ID: ${productId || 'Complete Plant'}`]);
    wsData.push([`Date of Download: ${downloadDate}`]);
    wsData.push([]); // empty row before table

    // Table headers
    wsData.push(['Date Time', 'Product ID', 'Status', 'Alerts']);

    // Table rows
    analyticsLogs.forEach((item) => {
      wsData.push([
        formatISODateTime(item.date),
        item.pid,
        item.status,
        item.alerts,
      ]);
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Abstraction');

    // Save as Excel file
    XLSX.writeFile(wb, `Borewell_Alert_Logs_${productId || 'Plant'}.xlsx`);
  };

  const fetchDeviceList = async () => {
    try {
      let response;

      if (userRole === 'admin') {
        response = await axiosInstance.get(
          `${baseUrl}/device/device-list?companyId=${selectedCompany.current}`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      } else {
        response = await axiosInstance.get(`${baseUrl}/device/device-list`, {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        });
      }
      if (response) {
        const data = response?.data?.data?.data;
        setDeviceList(data || []);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const fetchUserPermissions = async () => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/auth/user-permissions`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        const data = response?.data?.data;
        setUserPermissions(data);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const checkPermissions = (pid) => {
    if (!pid || pid === '') {
      toast({
        title: 'Please select a valid borewell',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setCanMonitor(false);
      return;
    }
    if (userRole === 'user') {
      const filteredDevice = userPermissions?.filter(
        (item) => item.productId === pid,
      );
      if (filteredDevice?.[0].monitor || filteredDevice?.[0].control) {
        setCanMonitor(true);
      } else {
        setCanMonitor(false);
        toast({
          title: `Access to device ${pid} has been denied.`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const fetchOverviewData = async () => {
    try {
      let response;

      if (userRole === 'admin') {
        if (!selectedCompany.current || selectedCompany.current === '') return;

        response = await axiosInstance.get(
          `${baseUrl}/device-readings/fetchOverviewData?companyId=${selectedCompany.current}`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      } else if (['companyUser', 'user'].includes(userRole)) {
        response = await axiosInstance.get(
          `${baseUrl}/device-readings/fetchOverviewData`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      }

      if (response) {
        const data = response?.data?.data?.data;
        updateLicenseData({
          borewellCount: Array.isArray(data) ? data.length : 0,
        });
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  function getMomentValue(value) {
    if (!value) return null;

    const parsed = moment.parseZone(value, moment.ISO_8601, true);
    if (parsed.isValid()) return parsed;

    const looseParsed = moment.parseZone(value);
    return looseParsed.isValid() ? looseParsed : null;
  }

  function formatBackendDate(value, format) {
    const parsed = getMomentValue(value);
    return parsed ? parsed.format(format) : '-';
  }

  function formatISODate(value) {
    return formatBackendDate(value, 'DD-MM-YYYY');
  }

  function formatAbstractionDate(value) {
    if (abstractionPeriod === 'yearly') {
      return formatBackendDate(value, 'YYYY');
    }

    if (abstractionPeriod === 'monthly') {
      return formatBackendDate(value, 'MMM YYYY');
    }

    return formatBackendDate(value, 'DD-MM-YYYY');
  }

  function formatAbstractionChartDate(value) {
    if (abstractionPeriod === 'yearly') {
      return formatBackendDate(value, 'YYYY');
    }

    if (abstractionPeriod === 'monthly') {
      return formatBackendDate(value, 'MMM YYYY');
    }

    return formatBackendDate(value, 'DD-MMM');
  }

  function formatISODateTime(value) {
    return formatBackendDate(value, 'DD-MM-YYYY hh:mm A');
  }

  function formatNowIST() {
    return moment().tz(IST_TIMEZONE).format('DD-MM-YYYY hh:mm A');
  }

  function formatTimeTo12Hour(timeStr) {
    if (!timeStr) return '-';

    const stringValue = String(timeStr);
    if (stringValue.includes('T')) {
      return formatBackendDate(stringValue, 'hh:mm A');
    }

    const parsedTime = moment(
      stringValue,
      ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'],
      true,
    );
    if (!parsedTime.isValid()) return stringValue;

    let hours = parsedTime.hour();
    const minutes = parsedTime.minute().toString().padStart(2, '0');

    let period = 'AM';

    if (hours >= 12) {
      period = 'PM';
      if (hours > 12) hours -= 12;
    } else if (hours === 0) {
      hours = 12;
    }

    const formattedHours = hours.toString().padStart(2, '0');
    return `${formattedHours}:${minutes} ${period}`;
  }

  const sanitizePdfValue = (value) => {
    if (value === undefined || value === null || value === '') return '-';
    return String(value).replace(/\s+/g, ' ').trim();
  };

  const formatReportDateRange = () => {
    const fromDate = startDate ? formatISODate(startDate) : 'N/A';
    const toDate = stopDate ? formatISODate(stopDate) : 'N/A';
    return `${fromDate} to ${toDate}`;
  };

  const getPdfMetaRows = (productId = selectedPid || 'Complete Plant') => [
    ['Company', localStorage.getItem('companyName') || '-'],
    ['Product ID', productId || 'Complete Plant'],
    ['Date Range', formatReportDateRange()],
    ['Generated On', `${formatNowIST()} IST`],
  ];

  const downloadTablePdf = ({ title, fileName, columns, rows, productId }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      toast({
        title: 'No data available to download',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = { left: 10, right: 10, top: 10, bottom: 12 };
    const tableWidth = pageWidth - margin.left - margin.right;
    const lineHeight = 4.2;
    const cellPadding = 2;
    const weights = columns.map((column) => column.width || 1);
    const totalWeight = weights.reduce((total, value) => total + value, 0);
    const columnWidths = weights.map(
      (weight) => (tableWidth * weight) / totalWeight,
    );
    const pageBottom = pageHeight - margin.bottom;

    let y = margin.top;

    const drawReportHeader = (isContinuation = false) => {
      pdf.setTextColor(20, 32, 50);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(
        isContinuation ? `${title} (continued)` : title,
        pageWidth / 2,
        y,
        {
          align: 'center',
        },
      );
      y += 8;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(80, 91, 110);

      getPdfMetaRows(productId).forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${label}:`, margin.left, y);
        pdf.setFont('helvetica', 'normal');
        pdf.text(sanitizePdfValue(value), margin.left + 28, y);
        y += 5;
      });

      y += 3;
    };

    const drawTableHeader = () => {
      const headerLineCounts = columns.map(
        (column, index) =>
          pdf.splitTextToSize(
            column.label,
            columnWidths[index] - cellPadding * 2,
          ).length,
      );
      const headerHeight =
        Math.max(...headerLineCounts) * lineHeight + cellPadding * 2;

      let x = margin.left;
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setDrawColor(203, 213, 225);

      columns.forEach((column, index) => {
        const width = columnWidths[index];
        const lines = pdf.splitTextToSize(
          column.label,
          width - cellPadding * 2,
        );
        pdf.setFillColor(45, 57, 78);
        pdf.rect(x, y, width, headerHeight, 'F');
        pdf.rect(x, y, width, headerHeight, 'S');
        pdf.setTextColor(255, 255, 255);
        pdf.text(lines, x + cellPadding, y + cellPadding + 3);
        x += width;
      });

      y += headerHeight;
    };

    const addPageWithHeader = () => {
      pdf.addPage();
      y = margin.top;
      drawReportHeader(true);
      drawTableHeader();
    };

    drawReportHeader();
    drawTableHeader();

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);

    rows.forEach((row) => {
      const wrappedCells = columns.map((column, index) =>
        pdf.splitTextToSize(
          sanitizePdfValue(row[index]),
          columnWidths[index] - cellPadding * 2,
        ),
      );
      const maxLines = Math.max(...wrappedCells.map((lines) => lines.length));
      let lineOffset = 0;

      while (lineOffset < maxLines) {
        if (y > pageBottom - 10) {
          addPageWithHeader();
        }

        const availableLines = Math.max(
          1,
          Math.floor((pageBottom - y - cellPadding * 2) / lineHeight),
        );
        const linesThisChunk = Math.min(maxLines - lineOffset, availableLines);
        const rowHeight = Math.max(
          8,
          linesThisChunk * lineHeight + cellPadding * 2,
        );

        let x = margin.left;
        pdf.setDrawColor(226, 232, 240);
        pdf.setTextColor(22, 32, 45);

        for (let index = 0; index < columns.length; index += 1) {
          const width = columnWidths[index];
          const lines = wrappedCells[index].slice(
            lineOffset,
            lineOffset + linesThisChunk,
          );
          pdf.rect(x, y, width, rowHeight);
          if (lines.length > 0) {
            pdf.text(lines, x + cellPadding, y + cellPadding + 3);
          }
          x += width;
        }

        y += rowHeight;
        lineOffset += linesThisChunk;

        if (lineOffset < maxLines) {
          addPageWithHeader();
        }
      }
    });

    const pageCount = pdf.internal.getNumberOfPages();
    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      pdf.setPage(pageNumber);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      pdf.text(
        `Page ${pageNumber} of ${pageCount}`,
        pageWidth - margin.right,
        pageHeight - 6,
        { align: 'right' },
      );
    }

    pdf.save(fileName);
  };

  const downloadPdfForBorewellAbstraction = () => {
    downloadTablePdf({
      title: `Borewell Water Abstraction (${abstractionPeriodLabel} Report)`,
      fileName: `Borewell_Water_Abstraction_Report_${
        selectedPid || 'Plant'
      }_${abstractionPeriodLabel}.pdf`,
      productId: selectedPid || 'Complete Plant',
      columns: [
        { label: abstractionDateHeader, width: 1 },
        { label: 'Well ID', width: 1.1 },
        { label: 'Opening Date', width: 1.7 },
        { label: 'Totalizer Opening Reading', width: 1.6 },
        { label: 'Closing Date', width: 1.7 },
        { label: 'Totalizer Closing Reading', width: 1.6 },
        { label: abstractionConsumptionHeader, width: 1.4 },
      ],
      rows: dailyAbstraction.map((item) => [
        formatAbstractionDate(item.date),
        item.pid,
        formatISODateTime(item.openingDate),
        item.openingTotalizer,
        formatISODateTime(item.closingDate),
        item.closingTotalizer,
        `${item.dailyConsumption ?? '-'} m3`,
      ]),
    });
  };

  const downloadPdfForPlantAbstraction = () => {
    downloadTablePdf({
      title: `Plant Water Abstraction (${abstractionPeriodLabel} Report)`,
      fileName: `Plant_Water_Abstraction_Report_${abstractionPeriodLabel}.pdf`,
      productId: 'Complete Plant',
      columns: [
        { label: abstractionDateHeader, width: 1.1 },
        { label: 'Totalizer Opening Reading', width: 1.7 },
        { label: 'Totalizer Closing Reading', width: 1.7 },
        { label: abstractionConsumptionHeader, width: 1.3 },
      ],
      rows: dailyAbstraction.map((item) => [
        formatAbstractionDate(item.date),
        item.openingTotalizer,
        item.closingTotalizer,
        `${item.dailyConsumption ?? '-'} m3`,
      ]),
    });
  };

  const downloadPdfForBorewellConsumption = () => {
    downloadTablePdf({
      title: 'Borewell Operation Log Report',
      fileName: `Borewell_Operation_Logs_${selectedPid || 'Plant'}.pdf`,
      productId: selectedPid || 'Complete Plant',
      columns: [
        { label: 'Date', width: 1 },
        { label: 'Well ID', width: 1 },
        { label: 'Mode', width: 1 },
        { label: 'On Time', width: 1.1 },
        { label: 'Off Time', width: 1.1 },
        { label: 'Duration', width: 1.2 },
      ],
      rows: analyticsLogs.map((item) => [
        formatISODate(item.date),
        item.pid,
        item.mode,
        formatTimeTo12Hour(item.onTime),
        formatTimeTo12Hour(item.offTime),
        item.duration,
      ]),
    });
  };

  const downloadPdfForEnergyConsumption = () => {
    downloadTablePdf({
      title: 'Borewell Energy Consumption Report',
      fileName: `Borewell_Energy_Consumption_${selectedPid || 'Plant'}.pdf`,
      productId: selectedPid || 'Complete Plant',
      columns: [
        { label: 'Date', width: 1.2 },
        { label: 'Well ID', width: 1.2 },
        { label: 'Unit Consumed (KWH)', width: 1.4 },
      ],
      rows: analyticsLogs.map((item) => [
        formatISODate(item.date),
        item.pid,
        item.unitConsumed,
      ]),
    });
  };

  const downloadPdfForAlertLogs = () => {
    downloadTablePdf({
      title: 'Borewell Alert Log Report',
      fileName: `Borewell_Alert_Logs_${selectedPid || 'Plant'}.pdf`,
      productId: selectedPid || 'Complete Plant',
      columns: [
        { label: 'Date Time', width: 1.5 },
        { label: 'Well ID', width: 1 },
        { label: 'Status', width: 0.9 },
        { label: 'Alerts', width: 3.2 },
      ],
      rows: analyticsLogs.map((item) => [
        formatISODateTime(item.date),
        item.pid,
        item.status,
        item.alerts,
      ]),
    });
  };

  const fetchBorewellData = async () => {
    if (!selectedPid || !startDate || !stopDate) {
      toast({
        title: 'Please select borewell and date range',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/borewellAbstraction?pid=${selectedPid}&start=${formattedStart}&end=${formattedStop}&period=${abstractionPeriod}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.length === 0) {
          setDailyAbstraction([]);
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setDailyAbstraction(response.data.data);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const fetchPlantData = async () => {
    if (!startDate || !stopDate) {
      toast({
        title: 'Please select date range',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;
    let response;

    try {
      if (userRole === 'admin') {
        response = await axiosInstance.get(
          `${baseUrl}/analytics-logs/plantAbstraction?companyId=${selectedCompany.current}&start=${formattedStart}&end=${formattedStop}&period=${abstractionPeriod}`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      } else {
        response = await axiosInstance.get(
          `${baseUrl}/analytics-logs/plantAbstraction?start=${formattedStart}&end=${formattedStop}&period=${abstractionPeriod}`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );
      }
      if (response) {
        if (response.data.data.length === 0) {
          setDailyAbstraction([]);
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setDailyAbstraction(response.data.data);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
    // E.g., fetch('/api/plant-data')
  };

  const fetchOperationLog = async (type) => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/analyticsLogs?pid=${selectedPid}&type=${type}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setAnalyticsLogs(response.data.data.data);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const fetchEnergyConsumption = async (type) => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/analyticsLogs?pid=${selectedPid}&type=${type}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setAnalyticsLogs(response.data.data.data);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const fetchAlertLog = async (type) => {
    const formattedStart = `${startDate}T00:00:00Z`;
    const formattedStop = `${stopDate}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/analyticsLogs?pid=${selectedPid}&type=${type}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setAnalyticsLogs(response.data.data.data);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setStopDate('');
    setSelectedPid('');
    setAbstractionPeriod('daily');
    setDailyAbstraction([]);
    setAnalyticsLogs([]);
    setCanMonitor(true);
  };

  // The main handler function
  const handleTabChange = (index) => {
    clearFilters();
    setActiveTab(index);
  };

  const filterAvgVllData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: formatBackendDate(item._time, 'HH:mm'),
        VLL: item.avg_vll || 0,
      }));

    setAvgVll(filtered);
  };

  const filterCurrentData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: formatBackendDate(item._time, 'HH:mm'),
        IR: item.current_r || 0,
        IY: item.current_y || 0,
        IB: item.current_b || 0,
      }));

    setCurrent(filtered);
  };

  const filterPowerFactorData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: formatBackendDate(item._time, 'HH:mm'),
        PFr: item.pf_r || 0,
        PFy: item.pf_y || 0,
        PFb: item.pf_b || 0,
      }));

    setPowerFactor(filtered);
  };

  const filterFrequencyData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: formatBackendDate(item._time, 'HH:mm'),
        Frequency: item.frequency || 0,
      }));

    setFrequency(filtered);
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get(`${baseUrl}/company/list`, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      if (response) {
        const data = response?.data?.data?.data;
        setCompanyData(data);
        if (!data || data.length <= 0) {
          toast({
            title: 'No companies found',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const filterVoltageData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: formatBackendDate(item._time, 'HH:mm'),
        VR: item.voltage_r || 0,
        VY: item.voltage_y || 0,
        VB: item.voltage_b || 0,
      }));

    setVoltage(filtered);
  };

  const filterKwhData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: formatBackendDate(item._time, 'HH:mm'),
        KWh: item.total_kwh || 0,
      }));

    setKwh(filtered);
  };

  const filterKwData = () => {
    const filtered = energyConsumptionGraph
      .sort((a, b) => new Date(a.date) - new Date(b.date)) // sort first
      .map((item) => ({
        date: formatBackendDate(item._time, 'HH:mm'),
        KW: item.total_kw || 0,
      }));

    setKw(filtered);
  };

  const handleTabChangeForEneryConsumption = (index) => {
    setactiveTabEnergyConsumption(index);

    switch (index) {
      case 0:
        filterAvgVllData();
        break;

      case 1:
        filterCurrentData();
        break;

      case 2:
        filterPowerFactorData();
        break;

      case 3:
        filterFrequencyData();
        break;

      case 4:
        filterVoltageData();
        break;

      case 5:
        filterKwhData();
        break;

      case 6:
        filterKwData();
        break;

      default:
        console.log('called');
    }
    // clearFilters();
  };

  const fetchPermissibleLimits = async () => {
    try {
      let response;
      if (userRole === 'admin') {
        response = await axiosInstance.get(
          `${baseUrl}/company/getPermissibleLimits?companyId=${selectedCompany.current}`,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
            },
          },
        );

        if (response) {
          const data = response?.data?.data;
          updateLicenseData({
            nocNumber: data[0]?.nocDetails?.nocNumber,
            limitPerDay: data[0].limitPerDay,
            limitPerYear: data[0].limitPerYear,
            nocStartDate: data[0]?.nocDetails?.nocExpiry?.startDate,
            nocEndDate: data[0]?.nocDetails?.nocExpiry?.endDate,
            companyLogoUrl: data[0]?.companyLogoUrl,
          });
        }
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  const fetchEnergyConsumptionReading = async () => {
    const formattedStart = `${startDateEnergyCons}T00:00:00Z`;
    const formattedStop = `${startDateEnergyCons}T23:59:59Z`;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/analytics-logs/energyConsumptionReading?pid=${selectedPid}&start=${formattedStart}&end=${formattedStop}`,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
          },
        },
      );
      if (response) {
        if (response.data.data.length === 0) {
          toast({
            title: 'No data found',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setenergyConsumptionGraph(response.data.data);
      }
    } catch (error) {
      notifyApiError(error, toast, 'Failed to load analytics data');
    }
  };

  useEffect(() => {
    if (userRole !== 'admin') {
      fetchDeviceList();
      fetchOverviewData();
    }
  }, []);

  useEffect(() => {
    if (userRole === 'user') {
      fetchUserPermissions();
    }
  }, []);

  useEffect(() => {
    if (userRole === 'admin') {
      fetchCompanies();
    }
  }, []);

  const pageBg = useColorModeValue('gray.50', 'dark.900');
  const bgColor = useColorModeValue('white', 'dark.800');
  const surfaceBg = useColorModeValue('white', 'dark.800');
  const tableHeaderBg = useColorModeValue('#f7faff', 'dark.700');
  const borderColor = useColorModeValue('gray.200', 'dark.600');
  const textPrimary = useColorModeValue('gray.800', 'gray.100');
  const textMuted = useColorModeValue('gray.600', 'gray.400');
  const fieldTextColor = useColorModeValue(
    'rgba(112, 116, 137, 1)',
    'gray.300',
  );
  const primaryColor = useColorModeValue('rgba(16, 114, 187, 1)', 'aqua.300');
  const analyticsTableSx = {
    th: {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      verticalAlign: 'top',
    },
    td: {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      verticalAlign: 'top',
    },
  };

  return (
    <Box p={6} bg={pageBg} color={textPrimary} minH="100vh" mt="65px">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '1.25rem',
          marginBottom: '2rem',
        }}
      >
        {/* Left side: Text */}
        <Text fontSize="xl" fontWeight="bold" color={textPrimary}>
          Analytics
        </Text>

        {/* Right side: Select */}
        {userRole === 'admin' && (
          <Select
            placeholder="Select Company"
            size="md"
            maxW="210px"
            onChange={(e) => {
              selectedCompany.current = e.target.value;
              fetchDeviceList();
              fetchOverviewData();
              fetchPermissibleLimits();
            }}
          >
            {companyData.map((item) => (
              <option key={item.productId} value={item._id}>
                {item.name}
              </option>
            ))}
          </Select>
        )}
      </div>

      {/* Tabs Section */}
      <Tabs
        index={activeTab}
        onChange={handleTabChange}
        variant="unstyled"
        w="100%"
      >
        <TabList
          bg={surfaceBg}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
          overflowX={{ base: 'auto', lg: 'hidden' }}
          overflowY="hidden"
          w="100%"
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.key}
              flex="1 0 0"
              minW={{ base: '190px', lg: 0 }}
              px={{ base: 4, xl: 6 }}
              py={3}
              justifyContent="center"
              fontWeight="semibold"
              fontSize="sm"
              whiteSpace="nowrap"
              color={activeTab === index ? primaryColor : textMuted}
              borderBottom={
                activeTab === index
                  ? `2px solid ${primaryColor}`
                  : '2px solid transparent'
              }
              _hover={{ color: primaryColor }}
              _focus={{ boxShadow: 'none' }}
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {/* Borewell Abstraction Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(5, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color={fieldTextColor}
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      setDailyAbstraction([]);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    View By
                  </Text>
                  <Select
                    color={fieldTextColor}
                    fontSize="16px"
                    value={abstractionPeriod}
                    onChange={(e) => {
                      setAbstractionPeriod(e.target.value);
                      setDailyAbstraction([]);
                    }}
                  >
                    {ABSTRACTION_PERIOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color={fieldTextColor}
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color={fieldTextColor}
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchBorewellData()}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(dailyAbstraction) &&
                dailyAbstraction.length > 0 && (
                  <Box
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="lg"
                  >
                    <Box maxH="400px" overflowX="auto" overflowY="auto">
                      <Table
                        variant="striped"
                        size="sm"
                        w="100%"
                        minW="900px"
                        tableLayout="fixed"
                        sx={analyticsTableSx}
                      >
                        <Thead bg={tableHeaderBg}>
                          <Tr>
                            <Th fontSize="12px">{abstractionDateHeader}</Th>
                            <Th fontSize="12px">Well ID</Th>
                            <Th fontSize="12px">Opening Date</Th>
                            <Th fontSize="12px">Totalizer Opening Reading</Th>
                            <Th fontSize="12px">Closing Date</Th>
                            <Th fontSize="12px">Totalizer Closing Reading</Th>
                            <Th fontSize="12px">
                              {abstractionConsumptionHeader}
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dailyAbstraction.map((row, i) => (
                            <Tr key={i}>
                              <Td>{formatAbstractionDate(row.date)}</Td>
                              <Td>{row.pid}</Td>
                              <Td>{formatISODateTime(row.openingDate)}</Td>
                              <Td>{row.openingTotalizer}</Td>
                              <Td>{formatISODateTime(row.closingDate)}</Td>
                              <Td>{row.closingTotalizer}</Td>
                              <Td>
                                {row.dailyConsumption}m<sup>3</sup>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>

                    {/* Buttons Container */}
                    <Flex mt={0} gap={4} p={4} justifyContent="flex-start">
                      <Button
                        onClick={downloadExcelForBorewellAbstraction}
                        leftIcon={<XlDownload style={{ width: '20px' }} />}
                        borderRadius="md"
                        variant="outline"
                        borderColor="rgba(16, 114, 187, 1)"
                        color="rgba(16, 114, 187, 1)"
                        fontSize="sm"
                      >
                        Download Excel
                      </Button>
                      <Button
                        onClick={downloadPdfForBorewellAbstraction}
                        leftIcon={<PdfDownload style={{ width: '20px' }} />}
                        borderRadius="md"
                        variant="outline"
                        borderColor="rgba(16, 114, 187, 1)"
                        color="rgba(16, 114, 187, 1)"
                        fontSize="sm"
                      >
                        Download PDF
                      </Button>
                    </Flex>
                  </Box>
                )}

              {/* Chart */}
              {Array.isArray(dailyAbstraction) &&
                dailyAbstraction.length > 0 && (
                  <Box mt={8}>
                    <Text
                      textAlign="center"
                      fontWeight="semibold"
                      mb={2}
                      color={textMuted}
                    >
                      Usage Data ({abstractionPeriodLabel})
                    </Text>

                    <Box
                      ref={chartRef0}
                      height="300px"
                      minW="300"
                      minH="200"
                      bg={surfaceBg}
                      borderRadius="xl"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={abstractionChartData}>
                          <CartesianGrid strokDasharray="4 4" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="Unit"
                            fill={primaryColor}
                            radius={[6, 6, 0, 0]}
                          >
                            <LabelList
                              dataKey="Unit"
                              content={renderDownloadOnlyBarLabel}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    <Flex justify="flex-start" mt={4}>
                      <Button
                        onClick={handleDownloadGraphPDF}
                        bg={primaryColor}
                        fontSize="14px"
                        color="white"
                        rounded="base"
                        _hover={{ bg: '#005a99' }}
                        leftIcon={<DownloadIcon />}
                        size="sm"
                        py={5}
                      >
                        Download Graph
                      </Button>
                    </Flex>
                  </Box>
                )}
            </Box>
          </TabPanel>

          {/* Plant Abstraction Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    View By
                  </Text>
                  <Select
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    value={abstractionPeriod}
                    onChange={(e) => {
                      setAbstractionPeriod(e.target.value);
                      setDailyAbstraction([]);
                    }}
                  >
                    {ABSTRACTION_PERIOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex align="end">
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchPlantData()}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {dailyAbstraction.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowX="auto"
                    overflowY="auto"
                  >
                    <Table
                      variant="striped"
                      size="sm"
                      w="100%"
                      minW="760px"
                      tableLayout="fixed"
                      sx={analyticsTableSx}
                    >
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th fontSize="12px">{abstractionDateHeader}</Th>
                          <Th fontSize="12px">Totalizer Opening Reading</Th>
                          <Th fontSize="12px">Totalizer Closing Reading</Th>
                          <Th fontSize="12px">
                            {abstractionConsumptionHeader}
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {dailyAbstraction.map((row, i) => (
                          <Tr key={i}>
                            <Td>{formatAbstractionDate(row.date)}</Td>
                            <Td>{row.openingTotalizer}</Td>
                            <Td>{row.closingTotalizer}</Td>
                            <Td>
                              {row.dailyConsumption}m<sup>3</sup>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForPlantAbstraction}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      onClick={downloadPdfForPlantAbstraction}
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}

              {/* Chart */}
              {Array.isArray(dailyAbstraction) &&
                dailyAbstraction.length > 0 && (
                  <Box mt={8}>
                    <Text
                      textAlign="center"
                      fontWeight="semibold"
                      mb={2}
                      color="gray.600"
                    >
                      Usage Data ({abstractionPeriodLabel})
                    </Text>
                    <Box
                      ref={chartRef1}
                      height="300px"
                      bg="white"
                      borderRadius="xl"
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={abstractionChartData}>
                          <CartesianGrid strokDasharray="4 4" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="Unit"
                            fill={primaryColor}
                            radius={[6, 6, 0, 0]}
                          >
                            <LabelList
                              dataKey="Unit"
                              content={renderDownloadOnlyBarLabel}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    <Flex justify="flex-start" mt={4}>
                      <Button
                        onClick={handleDownloadGraphPDF}
                        bg={primaryColor}
                        fontSize="14px"
                        color="white"
                        rounded="base"
                        _hover={{ bg: '#005a99' }}
                        leftIcon={<DownloadIcon />}
                        size="sm"
                        py={5}
                      >
                        Download Graph
                      </Button>
                    </Flex>
                  </Box>
                )}
            </Box>
          </TabPanel>

          {/* Borewell Operation Logs Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color="rgba(112, 116, 137, 1)"
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchOperationLog('operation')}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowX="auto"
                    overflowY="auto"
                  >
                    <Table
                      variant="striped"
                      size="sm"
                      w="100%"
                      minW="760px"
                      tableLayout="fixed"
                      sx={analyticsTableSx}
                    >
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th fontSize="12px">Date</Th>
                          <Th fontSize="12px">Well ID</Th>
                          <Th fontSize="12px">Mode</Th>
                          <Th fontSize="12px">On Time</Th>
                          <Th fontSize="12px">Off Time</Th>
                          <Th fontSize="12px">Duration</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analyticsLogs.map((row, i) => (
                          <Tr key={i}>
                            <Td>{formatISODate(row.date)}</Td>
                            <Td>{row.pid}</Td>
                            <Td>{row.mode}</Td>
                            <Td>{formatTimeTo12Hour(row.onTime)}</Td>
                            <Td>{formatTimeTo12Hour(row.offTime)}</Td>
                            <Td>{row.duration}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForBorewellConsumption}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      onClick={downloadPdfForBorewellConsumption}
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}

              {/* Chart */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box mt={8}>
                  <Text
                    textAlign="center"
                    fontWeight="semibold"
                    mb={2}
                    color="gray.600"
                  >
                    Usage Data (Daily)
                  </Text>
                  <Box
                    ref={chartRef2}
                    height="300px"
                    bg="white"
                    borderRadius="xl"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={borewellOperationChartData}>
                        <CartesianGrid strokeDasharray="4 4" />
                        <XAxis dataKey="date" />
                        <YAxis
                          tickFormatter={(value) => {
                            const hours = Math.floor(value / 3600);
                            const minutes = Math.floor((value % 3600) / 60);
                            return `${hours}h ${minutes}m`; // shorter format on Y-axis
                          }}
                        />
                        <Tooltip
                          formatter={(value, name, props) => {
                            const hours = Math.floor(value / 3600);
                            const minutes = Math.floor((value % 3600) / 60);
                            const seconds = Math.floor(value % 60);
                            return [
                              `${hours}h ${minutes}m ${seconds}s`,
                              'Duration',
                            ];
                          }}
                        />
                        <Bar
                          dataKey="value"
                          fill={primaryColor}
                          radius={[6, 6, 0, 0]}
                        >
                          <LabelList
                            dataKey="label"
                            content={renderDownloadOnlyBarLabel}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <Flex justify="flex-start" mt={4}>
                    <Button
                      onClick={handleDownloadGraphPDF}
                      bg={primaryColor}
                      fontSize="14px"
                      color="white"
                      rounded="base"
                      _hover={{ bg: '#005a99' }}
                      leftIcon={<DownloadIcon />}
                      size="sm"
                      py={5}
                    >
                      Download Graph
                    </Button>
                  </Flex>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Energy Consumption Logs Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color="rgba(112, 116, 137, 1)"
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchEnergyConsumption('energy_consumption')}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowX="auto"
                    overflowY="auto"
                  >
                    <Table
                      variant="striped"
                      size="sm"
                      w="100%"
                      minW="760px"
                      tableLayout="fixed"
                      sx={analyticsTableSx}
                    >
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th fontSize="12px">Date</Th>
                          <Th fontSize="12px">Well ID</Th>
                          <Th fontSize="12px">Unit Consumed (KWH)</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analyticsLogs.map((row, i) => (
                          <Tr key={i}>
                            <Td>{formatISODate(row.date)}</Td>
                            <Td>{row.pid}</Td>
                            <Td>{row.unitConsumed}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForEneryConsumption}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      onClick={downloadPdfForEnergyConsumption}
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}

              {/* Chart */}
              <Box mt={8}>
                {/* Date selector + button */}
                <Flex justify="left" align="center" gap={3}>
                  <Text
                    textAlign="center"
                    fontWeight="semibold"
                    mb={2}
                    color="gray.600"
                  >
                    Graph
                  </Text>
                  <Flex
                    opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                    pointerEvents={
                      canMonitor || userRole !== 'user' ? 'auto' : 'none'
                    }
                    justify="left"
                    align="center"
                    gap={3}
                  >
                    <Input
                      value={startDateEnergyCons}
                      type="date"
                      maxW="200px"
                      size="sm"
                      borderRadius="md"
                      borderColor="gray.300"
                      _hover={{ borderColor: 'gray.400' }}
                      onChange={(e) => setStartDateEnergyCons(e.target.value)}
                    />
                    <Button
                      bg={primaryColor}
                      color="white"
                      fontSize="14px"
                      size="sm"
                      rounded="base"
                      _hover={{ bg: '#005a99' }}
                      onClick={fetchEnergyConsumptionReading}
                    >
                      Submit
                    </Button>
                  </Flex>
                </Flex>
                {Array.isArray(energyConsumptionGraph) &&
                  energyConsumptionGraph.length > 0 && (
                    <Tabs
                      index={activeTabEnergyConsumption}
                      onChange={handleTabChangeForEneryConsumption}
                      variant="unstyled"
                    >
                      <TabList
                        bg="white"
                        // borderRadius="lg"
                        // border="1px solid"
                        // borderColor="gray.200"
                        boxShadow="sm"
                        overflow="hidden"
                        px={1}
                      >
                        {[
                          'VLL',
                          'Current',
                          'Power Factor',
                          'Frequency',
                          'Voltage',
                          'KWH',
                          'KW',
                        ].map((label, index) => (
                          <Tab
                            key={index}
                            px={6}
                            py={3}
                            fontWeight="semibold"
                            fontSize="sm"
                            color={
                              activeTabEnergyConsumption === index
                                ? 'blue.600'
                                : 'gray.600'
                            }
                            borderBottom={
                              activeTabEnergyConsumption === index
                                ? '2px solid rgba(0,106,184,1)'
                                : '2px solid transparent'
                            }
                            borderRadius="none"
                            _hover={{ color: 'blue.500' }}
                            _focus={{ boxShadow: 'none' }}
                          >
                            {label}
                          </Tab>
                        ))}
                      </TabList>
                      <TabPanels>
                        {/* Panel for Vll graph */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef0}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={avg_vll}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis
                                  dataKey="date"
                                  tickFormatter={(tick) => {
                                    return tick; // already formatted as "HH:mm", you can also use moment(tick).format('HH:mm')
                                  }}
                                />
                                <YAxis unit=" v" />{' '}
                                {/* shows V as unit on Y-axis */}
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'VLL',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                <Line
                                  type="monotone" // smooth line
                                  dataKey="VLL"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false} // hide data points if needed
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for current */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef1}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={current}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" amp" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'IR',
                                      type: 'line',
                                      color: '#8884d8',
                                    },
                                    {
                                      value: 'IY',
                                      type: 'line',
                                      color: '#82ca9d',
                                    },
                                    {
                                      value: 'IB',
                                      type: 'line',
                                      color: '#ffc658',
                                    },
                                  ]}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="IR"
                                  stroke="#8884d8"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="IY"
                                  stroke="#82ca9d"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="IB"
                                  stroke="#ffc658"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for power factor */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef2}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={powerFactor}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />

                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'PFr',
                                      type: 'line',
                                      color: '#8884d8',
                                    },
                                    {
                                      value: 'PFb',
                                      type: 'line',
                                      color: '#82ca9d',
                                    },
                                    {
                                      value: 'PFy',
                                      type: 'line',
                                      color: '#ffc658',
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="PFr"
                                  stroke="#8884d8"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="PFy"
                                  stroke="#82ca9d"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="PFb"
                                  stroke="#ffc658"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for frequency */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef3}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={frequency}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" hz" />
                                <Tooltip />

                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'Frequency',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="Frequency"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for voltage */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef4}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={voltage}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" v" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'VR',
                                      type: 'line',
                                      color: '#8884d8',
                                    },
                                    {
                                      value: 'VY',
                                      type: 'line',
                                      color: '#82ca9d',
                                    },
                                    {
                                      value: 'VB',
                                      type: 'line',
                                      color: '#ffc658',
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="VR"
                                  stroke="#8884d8"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="VY"
                                  stroke="#82ca9d"
                                  strokeWidth={2}
                                  dot={false}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="VB"
                                  stroke="#ffc658"
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for KWH */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef5}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={kwh}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" kwh" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'KWh',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="KWh"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>

                        {/* Panel for KW */}
                        <TabPanel>
                          {/* Graph container */}
                          <Box
                            ref={ecChartRef6}
                            height="300px"
                            bg="white"
                            borderRadius="xl"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={kw}>
                                <CartesianGrid strokeDasharray="4 4" />
                                <XAxis dataKey="date" />
                                <YAxis unit=" kw" />
                                <Tooltip />
                                <Legend
                                  verticalAlign="top"
                                  align="center"
                                  wrapperStyle={{ paddingBottom: 10 }}
                                  payload={[
                                    {
                                      value: 'KW',
                                      type: 'line',
                                      color: primaryColor,
                                    },
                                  ]}
                                />
                                {/* Three lines for three values */}
                                <Line
                                  type="monotone"
                                  dataKey="KW"
                                  stroke={primaryColor}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          {/* Download button */}
                          {Array.isArray(energyConsumptionGraph) &&
                            energyConsumptionGraph.length > 0 && (
                              <Flex justify="flex-start" mt={4}>
                                <Button
                                  onClick={handleDownloadGraphPDFForEC}
                                  bg={primaryColor}
                                  fontSize="14px"
                                  color="white"
                                  rounded="base"
                                  _hover={{ bg: '#005a99' }}
                                  leftIcon={<DownloadIcon />}
                                  size="sm"
                                  py={5}
                                >
                                  Download Graph
                                </Button>
                              </Flex>
                            )}
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  )}
              </Box>
            </Box>
          </TabPanel>

          {/* Alert Logs Panel */}
          <TabPanel>
            <Box
              border="1px solid"
              borderColor={borderColor}
              bg={bgColor}
              borderRadius="xl"
              p={6}
              mt={4}
            >
              {/* Date Filters */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={4}
                mb={6}
              >
                <Box>
                  <Text mb={2} fontWeight="semibold">
                    Select Borewell
                  </Text>
                  <Select
                    placeholder="Select Borewell..." // Changed placeholder for clarity
                    size="md"
                    flex="1"
                    fontSize="16px"
                    maxW="300px"
                    color="rgba(112, 116, 137, 1)"
                    onChange={(e) => {
                      setSelectedPid(e.target.value);
                      checkPermissions(e.target.value);
                    }}
                  >
                    {/* Dynamic rendering of options */}
                    {deviceList.map((item) => (
                      <option
                        size="sm"
                        key={item.productId}
                        value={item.productId}
                      >
                        {/* The displayed text format: "Main Gate (ATBC1234)" */}
                        {`${item.borewellName} (${item.productId})`}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (From)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                    value={startDate}
                  />
                </Box>
                <Box
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                >
                  <Text mb={2} fontWeight="semibold">
                    Select Date (To)
                  </Text>
                  <Input
                    color="rgba(112, 116, 137, 1)"
                    fontSize="16px"
                    type="date"
                    onChange={(e) => setStopDate(e.target.value)}
                    value={stopDate}
                  />
                </Box>
                <Flex
                  opacity={canMonitor || userRole !== 'user' ? 1 : 0.5}
                  pointerEvents={
                    canMonitor || userRole !== 'user' ? 'auto' : 'none'
                  }
                  align="end"
                >
                  <Button
                    w="full"
                    bg={primaryColor}
                    color="white"
                    rounded="base"
                    _hover={{ bg: '#005a99' }}
                    onClick={() => fetchAlertLog('alert')}
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid>

              {/* Table */}
              {Array.isArray(analyticsLogs) && analyticsLogs.length > 0 && (
                <Box
                  // The main container border is light blue/grey and slightly dashed/dotted (subtly implemented with a very light color and border-style on the container and internal rows)
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                >
                  <Box
                    maxH="400px" // 👈 set max height (adjust as needed)
                    overflowX="auto"
                    overflowY="auto"
                  >
                    <Table
                      variant="striped"
                      size="sm"
                      w="100%"
                      minW="760px"
                      tableLayout="fixed"
                      sx={analyticsTableSx}
                    >
                      {/* Table Header color matches the subtle blue-ish grey from the image */}
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th fontSize="12px">Date Time</Th>
                          <Th fontSize="12px">Well ID</Th>
                          <Th fontSize="12px">Status</Th>
                          <Th fontSize="12px">Alerts</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analyticsLogs.map((row, i) => (
                          <Tr key={i}>
                            <Td>{formatISODateTime(row.date)}</Td>
                            <Td>{row.pid}</Td>
                            <Td>{row.status}</Td>
                            <Td>{row.alerts}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Buttons Container */}
                  <Flex
                    mt={0} // Remove margin-top since the last row handles separation
                    gap={4}
                    p={4} // Add padding around the buttons to match the spacing in the image
                    justifyContent="flex-start" // Buttons are left-aligned
                  >
                    <Button
                      onClick={downloadExcelForAlertLogs}
                      leftIcon={<XlDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download Excel
                    </Button>
                    <Button
                      onClick={downloadPdfForAlertLogs}
                      leftIcon={<PdfDownload style={{ width: '20px' }} />}
                      borderRadius="md"
                      variant="outline"
                      borderColor="rgba(16, 114, 187, 1)"
                      color="rgba(16, 114, 187, 1)"
                      fontSize="sm"
                    >
                      Download PDF
                    </Button>
                  </Flex>
                </Box>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default AnalyticsDashboard;
